import { useEffect } from 'react';
import { industries } from '../data/solutions';
import { platformTopics } from '../data/platform';
import { accelerators } from '../data/accelerators';
import { caseStudies } from '../data/caseStudies';
import { trackPageView } from '../config/analytics';

const SITE_URL = 'https://turfai.in';
const OG_IMAGE = `${SITE_URL}/assets/TurfAI-logo.png`;

interface PageMeta {
  title: string;
  description: string;
  canonicalPath: string;
}

export const pageMeta: Record<string, PageMeta> = {
  'turfai': {
    title: 'TurfAI — Agentic workflow automation for the enterprise',
    description: 'TurfAI is the execution layer for enterprise AI: orchestrate agents, humans, and your systems into reliable, governable workflows — from trigger to action.',
    canonicalPath: '/',
  },
  'platform': {
    title: 'Platform — Agentic workflow automation | TurfAI',
    description: 'The TurfAI platform: a visual workflow builder with agentic AI, RAG, integrations, and enterprise governance on one auditable execution layer.',
    canonicalPath: '/platform',
  },
  ...Object.fromEntries(
    platformTopics.map((t) => [
      t.pageId,
      { title: t.metaTitle, description: t.metaDescription, canonicalPath: `/platform/${t.slug}` },
    ])
  ),
  'solutions': {
    title: 'Solutions | TurfAI',
    description: 'TurfAI solutions for healthcare, BFSI, insurance, HR, IT operations, legal, telecom, manufacturing, education and accounts payable.',
    canonicalPath: '/solutions',
  },
  ...Object.fromEntries(
    industries.map((ind) => [
      ind.pageId,
      { title: ind.metaTitle, description: ind.metaDescription, canonicalPath: `/solutions/${ind.slug}` },
    ])
  ),
  'resources': {
    title: 'Resources | TurfAI',
    description: 'Case studies of TurfAI in production — healthcare, BFSI, HR, telecom, insurance, education and more — and the docs to build on it.',
    canonicalPath: '/resources',
  },
  ...Object.fromEntries(
    accelerators.map((a) => [a.pageId, { title: a.metaTitle, description: a.metaDescription, canonicalPath: a.path }])
  ),
  ...Object.fromEntries(
    caseStudies.map((c) => [c.pageId, { title: `${c.title} — Case study | TurfAI`, description: c.metaDescription, canonicalPath: `/resources/${c.slug}` }])
  ),
  'schedule-demo': {
    title: 'Schedule a demo | TurfAI',
    description: 'Schedule a 30-minute TurfAI walkthrough on your own workflow.',
    canonicalPath: '/schedule-demo',
  },
  'contact-support': {
    title: 'Support | TurfAI',
    description: 'Get help with TurfAI and its accelerators from the team that runs them.',
    canonicalPath: '/contact',
  },
  'about': {
    title: 'About | TurfAI',
    description: 'Ariviti brings together Integra Micro Software Services and OpenTurf Technologies — two decades of building and running critical systems, and the company behind TurfAI.',
    canonicalPath: '/about',
  },
  'privacy-policy': {
    title: 'Privacy Policy | TurfAI',
    description: 'Read the TurfAI privacy policy to understand how we collect, use, and protect your data.',
    canonicalPath: '/privacy-policy',
  },
  'terms-of-service': {
    title: 'Terms of Service | TurfAI',
    description: 'Read the TurfAI terms of service governing your use of our platform and services.',
    canonicalPath: '/terms-of-service',
  },
  'security': {
    title: 'Security & Trust | TurfAI',
    description: 'How TurfAI protects your data: Data Shield tokenisation, layered controls across all 10 OWASP GenAI risks, and deployment inside your own boundary.',
    canonicalPath: '/security',
  },
  'not-found': {
    title: 'Page Not Found | TurfAI',
    description: 'The page you are looking for does not exist.',
    canonicalPath: '/',
  },
};

function setMetaTag(attribute: string, value: string, content: string) {
  let tag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export function usePageMeta(currentPage: string) {
  useEffect(() => {
    const meta = pageMeta[currentPage] || pageMeta['turfai'];
    const canonicalUrl = `${SITE_URL}${meta.canonicalPath}`;

    // Update document title
    document.title = meta.title;

    // Update meta description
    setMetaTag('name', 'description', meta.description);

    // Update canonical link
    let canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.rel = 'canonical';
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.href = canonicalUrl;

    // Open Graph tags
    setMetaTag('property', 'og:title', meta.title);
    setMetaTag('property', 'og:description', meta.description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'TurfAI');
    setMetaTag('property', 'og:image', OG_IMAGE);

    // Twitter Card tags
    setMetaTag('name', 'twitter:card', 'summary');
    setMetaTag('name', 'twitter:title', meta.title);
    setMetaTag('name', 'twitter:description', meta.description);
    setMetaTag('name', 'twitter:image', OG_IMAGE);
    setMetaTag('name', 'twitter:site', '@ArivitiTech');

    // JSON-LD structured data
    let scriptTag = document.querySelector('script[data-page-schema]') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.setAttribute('data-page-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          'name': 'TurfAI',
          'legalName': 'Ariviti Technologies Private Limited',
          'url': SITE_URL,
          'logo': OG_IMAGE,
          'sameAs': [
            'https://www.linkedin.com/company/turfai',
            'https://x.com/ArivitiTech',
            'https://www.linkedin.com/company/ArivitiTech/',
          ],
          'contactPoint': {
            '@type': 'ContactPoint',
            'email': 'hello@ariviti.com',
            'contactType': 'customer support',
          },
        },
        {
          '@type': 'WebSite',
          'name': 'TurfAI',
          'url': SITE_URL,
        },
        {
          '@type': 'WebPage',
          'name': meta.title,
          'description': meta.description,
          'url': canonicalUrl,
          'isPartOf': {
            '@type': 'WebSite',
            'url': SITE_URL,
          },
        },
      ],
    });

    // GA4 page_view for this SPA route (no-op unless analytics is configured).
    trackPageView(meta.canonicalPath, meta.title);
  }, [currentPage]);
}
